/* eslint-disable indent */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, LucideCreditCard as IdCard } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import Button from './ui/Button';
import { supabase } from '../supabaseClient';
import CameraCapture from './CameraCapture';

interface EdgeFunctionResponse {
	success: boolean;
	message: string;
	verificationId?: string;
}

const VerificationForm: React.FC = () => {
	const navigate = useNavigate();
	const [ghanaCardId, setGhanaCardId] = useState('');
	const [selfieFile, setSelfieFile] = useState<File | null>(null);
	const [hasConsent, setHasConsent] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [result, setResult] = useState<EdgeFunctionResponse | null>(null);

	const toBase64 = (file: File): Promise<string> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				try {
					const result = reader.result as string;
					resolve(result.split(',')[1]);
				} catch {
					reject(new Error('Failed to parse base64'));
				}
			};
			reader.onerror = () => reject(new Error('Failed to read file'));
			reader.readAsDataURL(file);
		});

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!ghanaCardId || !selfieFile || !hasConsent) {
			alert('Please fill all fields and provide consent');
			return;
		}

		setIsLoading(true);
		setResult(null);

		try {
			// Convert selfie to base64 and send directly
			const base64Image = await toBase64(selfieFile);

			// Call Supabase Edge Function with base64 image
			const { data, error } = await supabase.functions.invoke('verify-identity', {
				body: {
					ghanaCardId,
					selfieImage: base64Image,
					hasConsent,
				},
			});

			if (error) {
				console.error('Edge function error:', error);
				setResult({
					success: false,
					message: error.message || 'Verification failed due to a technical error.',
				});
				return;
			}

			setResult(data);
		} catch (error: any) {
			console.error('Verification error:', error);
			setResult({
				success: false,
				message: error.message || 'Failed to upload image. Please try again.',
			});
		} finally {
			setIsLoading(false);
		}
	};

	const formatGhanaCardId = (value: string) => {
		const cleaned = value.replace(/[^A-Za-z0-9]/g, '');
		if (cleaned.length <= 3) return cleaned.toUpperCase();
		if (cleaned.length <= 12) return `${cleaned.slice(0, 3).toUpperCase()}-${cleaned.slice(3)}`;
		return `${cleaned.slice(0, 3).toUpperCase()}-${cleaned.slice(3, 12)}-${cleaned.slice(12, 13)}`;
	};

	const handleCardIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formatted = formatGhanaCardId(e.target.value);
		if (formatted.length <= 15) setGhanaCardId(formatted);
	};

	if (result) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center ">
				<div className="max-w-2xl lg:w-lg h-auto m-auto ">
					<div className="bg-white rounded-2xl shadow-xl p-5 text-center">
						{result.success ? (
							<>
								<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
									<CheckCircle className="w-8 h-8 text-green-600" />
								</div>
								<h2 className="text-h2 font-bold text-green-600 mb-2">Verification Successful</h2>
								<p className="text-body text-cool-gray mb-2">{result.message}</p>
							</>
						) : (
							<>
								<div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
									<XCircle className="w-8 h-8 text-red-600" />
								</div>
								<h2 className="text-h2 font-bold text-red-600 mb-4">Verification Failed</h2>
								<p className="text-body text-cool-gray mb-6">{result.message}</p>
							</>
						)}
						<div className="space-y-3">
							<Button className="w-full" onClick={() => window.location.reload()}>
								Try Again
							</Button>
							<Button variant="ghost" className="w-full" onClick={() => navigate('/')}>
								Back to Home
							</Button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="h-screen bg-gray-50 pt-6 flex flex-col">
			<div className="max-w-2xl mx-auto flex flex-col flex-1 w-full gap-6">
				{/* Header */}
				<div className="flex items-center gap-4">
					<button onClick={() => navigate(-1)} className="p-4 rounded-lg hover:bg-gray-200">
						<ArrowLeft className="w-5 h-5 text-cool-gray" />
					</button>
					<div>
						<h1 className="md:text-xl font-bold text-midnight-blue">Identity Verification</h1>
						<p className="text-sm text-cool-gray">
							Verify your Ghana Card using facial recognition
						</p>
					</div>
				</div>

				{/* Body */}
				<div className="bg-white rounded-t-2xl md:rounded-2xl p-8 md:p-12 shadow-xl flex-1 flex flex-col">
					{isLoading ? (
						<LoadingSpinner message="Verifying your identity..." />
					) : (
						<form onSubmit={handleSubmit} className="h-full flex flex-col flex-1">
							<div className="flex flex-col flex-1 justify-between gap-6">
								{/* Ghana Card */}
								<div className="">
									<label className="text-base mb-2">Ghana Card ID</label>
									<div className="relative mt-2">
										<IdCard className="absolute left-5 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
										<input
											type="text"
											value={ghanaCardId}
											onChange={handleCardIdChange}
											placeholder="GHA-123456789-0"
											className="w-full pl-14 pr-4 py-3 border border-gray-200 rounded-lg text-sm"
											required
										/>
									</div>
									<p className="text-xs text-cool-gray mt-1 ml-1">
										Enter your 13-digit Ghana Card ID (e.g., GHA-123456789-0)
									</p>
								</div>

								{/* Take Picture */}
								<CameraCapture onCapture={setSelfieFile} disabled={isLoading} />

								{/* Consent */}
								<div className="bg-gray-50 rounded-lg p-4">
									<label className="flex items-start gap-3 text-sm">
										<input
											type="checkbox"
											checked={hasConsent}
											onChange={e => setHasConsent(e.target.checked)}
											className="mt-1 w-6 h-6 text-brand-orange border-gray-300 rounded focus:ring-brand-yellow"
											required
										/>
										<div className="text-small text-cool-gray">
											<p className="text-body font-medium mb-1 text-midnight-blue">
												Data Processing Consent
											</p>
											<p className="text-xs">
												I consent to the processing of my Ghana Card ID and facial image for
												identity verification purposes. I understand that this data will be used
												solely for verification and will not be stored permanently.
											</p>
											<p className="mt-3 text-xs">
												By proceeding, you agree to our{' '}
												<button
													type="button"
													onClick={() => navigate('/privacy')}
													className="text-ghana-red hover:underline"
												>
													Privacy Policy
												</button>
											</p>
										</div>
									</label>
								</div>

								{/* Submit Button */}
								<Button
									type="submit"
									className="w-full btn-primary py-4"
									disabled={!ghanaCardId || !selfieFile || !hasConsent || isLoading}
								>
									Verify Identity
								</Button>
							</div>
						</form>
					)}
				</div>
			</div>
		</div>
	);
};

export default VerificationForm;
